import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useQuery } from "@apollo/client";
import dayjs from "dayjs";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import DataTable from "components/DataTable";
import DatePicker from "components/DatePicker";
import Header from "components/Header";
import Select from "components/Select";
import StatusModal from "components/StatusModal";
import { CloudDownloadSVGIcon, LoadingSpinnerSVG } from "components/SVGIcons";
import { FILTER_BY_LIST, REPORTS_HEADER_LIST, REPORT_TYPE_LIST } from "constants/haulpay-broker";
import { showErrorMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import MainLayout from "layouts/Main";
import { IReportForm } from "models/HaulPay";
import { getCarrierReport, getHistoryTransactionAPI, getReportURL } from "services/api/haulpay";
import { selectUserDetails } from "store/user/selector";
import { QUERY_MY_REPORTS, QUERY_MY_REPORT_DETAILS } from "./query";
import styles from "./style.module.scss";
import { useUserFeature } from "hooks/useUserFeature";

interface IReportList {
  id: number;
  created: string;
  reportType: string;
  details: string;
}

export default function BrokerReports() {
  const { isDesktop } = useDeviceType();
  const userDetails = useSelector(selectUserDetails);
  const { isCarrier } = useUserFeature();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportList, setReportList] = useState<IReportList[]>([]);
  const [selectedId, setSelectedId] = useState<number>();
  const [isConfirmModalOpen, toggleConfirmModal] = useToggle();

  const tableRef = useRef<HTMLDivElement | null>(null);
  const [isScroll, setIsScroll] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [endCursor, setEndCursor] = useState();

  const { data, refetch } = useQuery(QUERY_MY_REPORTS, { variables: { sortBy: "-created" } });
  const { refetch: myReportRefetch, loading } = useQuery(QUERY_MY_REPORT_DETAILS, {
    variables: { id: selectedId },
    skip: !selectedId,
    fetchPolicy: "cache-and-network"
  });

  const {
    control,
    formState: { isSubmitting },
    handleSubmit
  } = useForm<Partial<IReportForm>>({
    values: {
      reportType: REPORT_TYPE_LIST[0]
    }
  });

  const onSubmit = useCallback(
    async (data: IReportForm) => {
      const formData = {
        factoring_id: userDetails?.factoring_id,
        report_type: data.reportType.value,
        created_gte: data.createdGte
          ? dayjs(data.createdGte).toISOString()
          : dayjs().subtract(30, "day").toISOString(),
        created_lte: data.createdLte ? dayjs(data.createdLte).toISOString() : dayjs().toISOString()
      };

      try {
        const response = isCarrier
          ? await getCarrierReport({
              datetime_start: data.createdGte
                ? dayjs(data.createdGte).toISOString()
                : dayjs().subtract(30, "day").toISOString(),

              datetime_end: data.createdLte
                ? dayjs(data.createdLte).toISOString()
                : dayjs().toISOString()
            })
          : data.reportType.value !== "wallet_transaction"
            ? await getReportURL(formData)
            : await getHistoryTransactionAPI({
                download: true,
                created_gte: formData.created_gte,
                created_lte: formData.created_lte
              });
        if (!response) {
          showErrorMessage("Please try again.");
        } else {
          await refetch();
          toggleConfirmModal();
        }
      } catch (error: any) {
        showErrorMessage(error.message);
      }
    },
    [isCarrier]
  );

  const downloadReport = async (id: number) => {
    setSelectedId(id);
    try {
      const response = await myReportRefetch({
        id: id
      });
      const url = response?.data?.report?.downloadUrl;
      if (url) {
        window.open(url, "_blank");
      }
      setSelectedId(undefined);
    } catch (error: any) {
      showErrorMessage(error.message);
    }
  };

  const onScroll = async () => {
    if (tableRef.current) {
      const { scrollTop, offsetHeight, scrollHeight } = tableRef.current;
      if (scrollTop + offsetHeight + 1 >= scrollHeight && hasNextPage) {
        setIsScroll(true);
        await refetch({
          orderBy: "-time_posted",
          first: 20,
          after: endCursor
        });
      }
    }
  };

  useEffect(() => {
    if (data) {
      if (isScroll) {
        setReportList((prev) => [...prev, ...data.myReports.data]);
      } else {
        setReportList(data.myReports.data);
      }
      setHasNextPage(data.myReports.pageInfo.hasNextPage);
      setEndCursor(data.myReports.pageInfo.endCursor);
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, []);

  const enhancedReportList = useMemo(() => {
    return reportList.map((data) => ({
      ...data,
      action: {
        downloadReport: () => {
          downloadReport(data.id);
        },
        isFetching: loading,
        selectedId: selectedId,
        id: data.id
      }
    }));
  }, [reportList, loading, downloadReport]);

  const reportHeaderList = useMemo(() => REPORTS_HEADER_LIST, [loading]);

  return (
    <MainLayout desktopTitle="Reports" customHeader={<Header label="Reports" isHideDesktop />}>
      <div className={styles.reportsContainer} ref={tableRef} onScroll={onScroll}>
        <form className={styles.transaction} onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="reportType"
            control={control}
            render={({ field: { value = REPORT_TYPE_LIST[0], onChange } }) => (
              <Select
                label="&nbsp;&nbsp;Transactions"
                options={REPORT_TYPE_LIST}
                value={value}
                onChange={onChange}
                btnClass="!w-72"
                disabled={isCarrier}
              />
            )}
          />

          <div className={styles.dateRange}>
            <Controller
              name="filterBy"
              control={control}
              render={({ field: { value = FILTER_BY_LIST[0], onChange } }) => (
                <Select
                  label="&nbsp;&nbsp;Filter by"
                  options={FILTER_BY_LIST}
                  value={value}
                  onChange={onChange}
                  className="mobile:hidden"
                />
              )}
            />

            <span className="mb-3.5 mobile:hidden">OR</span>

            <Controller
              name="createdGte"
              control={control}
              render={({ field: { value = startDate, onChange } }) => (
                <DatePicker
                  label="&nbsp;&nbsp;Start Date"
                  placeholder="Select Date"
                  value={value}
                  onChange={(value) => {
                    onChange(value);
                    setStartDate(value);
                  }}
                  inputClass="!desktop:pb-3 !desktop:pt-3.5"
                />
              )}
            />

            <Controller
              name="createdLte"
              control={control}
              render={({ field: { value = endDate, onChange } }) => (
                <DatePicker
                  label="&nbsp;&nbsp;End Date"
                  placeholder="Select Date"
                  value={value}
                  onChange={(value) => {
                    onChange(value);
                    setEndDate(value);
                  }}
                  inputClass="!desktop:pb-3 !desktop:pt-3.5"
                />
              )}
            />
            {isDesktop ? (
              <Button
                type="submit"
                className="h-12 !border-y-2 !border-primary"
                loading={isSubmitting}>
                Request Report
              </Button>
            ) : null}
          </div>

          {!isDesktop ? (
            <Button
              type="submit"
              className="h-12 !border-y-2 !border-primary w-full mt-6"
              loading={isSubmitting}>
              Request Report
            </Button>
          ) : null}
        </form>

        <div className={styles.reportsWrapper}>
          {isDesktop ? (
            <DataTable
              data={enhancedReportList}
              headers={reportHeaderList}
              sortDisableList={["actions"]}
            />
          ) : (
            <>
              {enhancedReportList.length ? (
                enhancedReportList.map((item) => (
                  <ul key={item.id} className={styles.reportList}>
                    <li>
                      <span>Requested on</span>
                      <span>{dayjs(item.created).format("YYYY-MM-DD hh:ss A")}</span>
                    </li>
                    <li>
                      <span>Type</span>
                      <span className="capitalize">{item.reportType}</span>
                    </li>
                    <li>
                      <span>Details</span>
                      <span>{item.details}</span>
                    </li>
                    <li className="items-center">
                      <span>Actions</span>
                      <button
                        type="button"
                        className={styles.button}
                        onClick={() => downloadReport(item.id)}>
                        {loading ? (
                          <LoadingSpinnerSVG className="inline size-3 animate-spin text-text fill-primary mx-auto" />
                        ) : (
                          <CloudDownloadSVGIcon className="flex-none text-info" />
                        )}
                      </button>
                    </li>
                  </ul>
                ))
              ) : (
                <h3 className={styles.noReports}>No reports</h3>
              )}
            </>
          )}
        </div>

        <StatusModal
          title="Success"
          isModalOpen={isConfirmModalOpen}
          toggleModal={toggleConfirmModal}
          backdropClick={toggleConfirmModal}
          content="We have received your request. Your report will be available soon."
        />
      </div>
    </MainLayout>
  );
}
